export interface Project {
  id: string;
  title: string;
  description?: string;
  subtitle?: string;
  tagline?: string;
  image: string;
  technologies: string[];
  githubUrl?: string;
  liveUrl?: string;
  isFeatured?: boolean;
  companyLogo?: string;
  award?: string;
  caseStudy?: {
    hook?: string;
    sections: {
      title: string;
      content?: string;
      keyOutcomes?: string[];
      solutionItems?: string[];
      solutionImage?: string;
      solutionImages?: (string | string[])[];
      subsections?: {
        title: string;
        content: string;
      }[];
    }[];
    researchDiscovery?: string;
    researchTakeaways?: string[];
    userJourneyMapping?: string;
    platformUsageAnalysis?: string;
    strategicPivot?: string;
    designDecisions?: string;
    designPrinciples?: string[];
    validationIteration?: string;
    lessonsLearned?: string;
  };
}

// Current work at Instrumental
export const currentProjects: Project[] = [
  {
    id: 'unit-filter',
    title: 'Unit Filter Redesign',
    description: 'Reconstructing the logic for unit filters with clear hierarchy, streamlining complex manufacturing data analysis into intuitive user experiences.',
    image: '/img/instrumental-2.gif',
    technologies: ['Complex System Redesign', 'Data Analytics UX', 'Usability Optimization', 'User Adoption'],
    caseStudy: {
      sections: [
        {
          title: "The problem",
          content: "Unit filtering is Instrumental's most critical user interface - serving over 3,000 monthly investigations by 110+ manufacturing engineers - yet most non-power users struggle to understand or use its full capabilities. It is powerful but broken."
        },
        {
          title: "My role",
          content: "Led research and design"
        },
        {
          title: "Project duration",
          content: "2 months"
        },
        {
          title: "Completion date",
          content: "March 2025"
        },
        {
          title: "Impact and key outcomes",
          content: "Investigation setup time is significantly reduced while filter confusion is minimized, making complex manufacturing analysis accessible to all.",
          keyOutcomes: [
            "**Organized filter hierarchy** eliminates endless scrolling through infinite dropdown lists and reduces empty filter results from **10%** to near zero through improved interaction design",
            "**Streamlined filter interactions** improve investigation efficiency by over **50%**, enabling manufacturing teams to replace tedious multi-step workflows with intuitive data discovery",
            "**Enhanced feature visibility** expands Multi-SKU filtering beyond power users to broader teams, creating competitive advantages for enterprise sales conversations"
          ]
        },
        {
          title: "Solution",
          solutionItems: [
            "Filter Hierarchy Established:\n\nBefore: Long scroll that difficult to understand\n\nAfter: Elevated 5 primary filters while organizing secondary options in structured tiers",
            "Date Filter Consolidation:\n\nBefore: Long list of station timestamp filters confused users alongside 'Date seen' filter, or were completely ignored\n\nAfter: Consolidated all date selections into one unified menu where users specify whether dates refer to any, all or specific inspections",
            "Multi-Attribute Selection:\n\nOverwhelming list of variations required user to navigate multiple steps to narrow down intended units\n\nAfter: Unified SKU filter allows users to specify all variation requirements in one view with multi-select capabilities"
          ],
          solutionImages: [
            ["/img/filter-hierarchy-2.gif", "/img/filter-hierarchy-1.gif"],
            ["/img/Grouping-before.png", "/img/logical-grouping-after.gif"],
            ["/img/multiselect-1.png", "/img/multiselect-after.png"]
          ]
        }
      ]
    }
  },
  {
    id: 'framework-transitioning',
    title: 'Framework Transition',
    description: 'Comprehensive design system and workflow reimagination that eliminated development guesswork while creating scalable foundation.',
    image: '/img/framework.png',
    technologies: ['React Migration', 'Design Tokens', 'Component Library', 'Dev Handoff'],
    caseStudy: {
      sections: [
        {
          title: "The problem",
          content: "Years of inconsistent design language and ambiguous engineering handoffs have created a fragmented platform with conflicting interaction patterns and usability issues that slow development velocity while creating a poor user experience that's difficult to navigate."
        },
        {
          title: "My role",
          content: "Led design system advocacy, component library research, and platform-wide design standardization."
        },
        {
          title: "Project duration",
          content: "6 months"
        },
        {
          title: "Completion date",
          content: "July 2024"
        },
        {
          title: "Impact and key outcomes",
          content: "Established foundational design system infrastructure that unified platform experience and accelerated development velocity across engineering teams.",
          keyOutcomes: [
            "**Standardized component library and design tokens** eliminated inconsistent implementations, reducing engineering decision-making overhead and accelerating feature development",
            "Unified interaction patterns, terminology, and visual language across **4 key product surfaces**, transforming fragmented user experience into cohesive manufacturing intelligence platform",
            "**Clear design specifications and component documentation** reduced handoff ambiguity, enabling more predictable development cycles and fewer design-related revisions"
          ]
        },
        {
          title: "Solution",
          solutionItems: [
            "Implementation Strategy:\n\nBefore: Poor information architecture with non-standardized components across the platform\n\nAfter, Rebuilt pages with clear visual hierarchy and consistent component implementation"
          ],
          solutionImages: [
            "/img/monitors-page-before.png",
            "/img/monitors-page-after.png"
          ]
        }
      ]
    }
  }
];

// Previous work projects
export const previousProjects: Project[] = [
  {
    id: 'resourcex',
    title: 'ResourceX Priority Based Budgeting',
    description: '<strong>ResourceX</strong> – Budgeting platform for efficient public-sector resource allocation.',
    image: '/img/resourceX-1.png',
    technologies: ['Public Sector', 'Budget Optimization', 'Data Visualization', 'Decision Intelligence'],
    companyLogo: 'ResourceX',
    caseStudy: {
      sections: [
        {
          title: "The problem",
          content: "Government budgeting offices struggle with ResourceX's complex, time-consuming workflows that create barriers to adopting priority-based budgeting methodology. The platform's developer-centric design leads to user frustration, inefficient resource allocation processes, and resistance to adopting modern budgeting approaches."
        },
        {
          title: "My role",
          content: "Led UX design for the priority-based budgeting interface and program evaluation workflows"
        },
        {
          title: "Project duration",
          content: "6 months"
        },
        {
          title: "Completion date",
          content: "June 2023"
        },
        {
          title: "Impact",
          content: "Redesigned government budgeting platform that was nominated for UX Design Award and led to strategic acquisition by S&P 500 company Tyler Technologies.",
          keyOutcomes: [
            "Eliminated annual setup burden and streamlined complex workflows, increasing government user adoption across **60+ government clients**",
            "Advanced priority-based budgeting adoption in public sector, replacing traditional line-item approaches with data-driven decision making",
            "Created consolidated program views and intuitive cost allocation tools that led to strategic acquisition by **S&P 500 company Tyler Technologies**"
          ]
        },
        {
          title: "Solution",
          solutionItems: [
            "Consolidated Program Inventory View\n\nA centralized page for users to see the overview, the cost allocation, and the quartile scores for all the programs.",
            "Intuitive Cost Allocation\n\nStreamlined interface for effortless selection, viewing, adding, resetting, and updating the cost associated with each program.",
            "New Scoring Module\n\nDrastically reduces time needed to score programs with keyboard interaction and prominent Call-to-Action.",
            "Budget Request Summary\n\nAdvanced analytics that allows the managing office to view, track, compare and unpack insights from data."
          ],
          solutionImages: [
            "/img/resourcex-program.png",
            "/img/resourcex-cost.png",
            "/img/resourcex-score.png",
            "/img/resourcex-summary.png"
          ]
        }
      ]
    }
  },
  {
    id: 'growlink',
    title: 'Growlink Agricultural Cultivation System',
    description: '<strong>Growlink</strong> – Cultivation system unifying IoT data and environmental controls.',
    image: '/img/growlink.png',
    technologies: ['IoT Integration', 'Real-time Monitoring', 'Agricultural Automation', 'Growth Optimization'],
    companyLogo: 'Growlink',
    caseStudy: {
      sections: [
        {
          title: "The problem",
          content: "The existing platform served primarily engineering users with specialized sensor knowledge. The product owner needed a redesign to make the system accessible to non-technical cultivators and expand the user base."
        },
        {
          title: "My role",
          content: "Led research and UX design"
        },
        {
          title: "Project duration",
          content: "3 months"
        },
        {
          title: "Completion date",
          content: "Sept 2022"
        },
        {
          title: "Impact",
          content: "Through strategic redesign and systematic approach, turned Growlink's complex cultivation platform into an accessible, scalable solution that accelerated development efficiency and expanded market reach.",
          keyOutcomes: [
            "Established scalable design system that reduced future feature development time by **80%** (dark mode: 2 weeks → 3 days)",
            "Delivered comprehensive redesign addressing core usability issues for broader market adoption across diverse cultivation facilities", 
            "Created modular framework enabling consistent user experience and accelerated development efficiency for agricultural technology platform"
          ]
        },
        {
          title: "Solution",
          solutionItems: [
            "Room Dashboard\n\nDesigned flexible visualization modules allowing users to customize data displays based on their specific needs. Created focused mobile views that prioritize critical information and eliminate visual clutter for on-site monitoring.",
            "Facility Dashboard\n\nImplemented a 12-column grid system with standardized modules to display facility-wide metrics. The systematic approach ensures consistent scaling and enables customizable layouts across different facility sizes.",
            "Dark Mode & Mobile Optimization\n\nDeveloped dark mode functionality and mobile-responsive design based on user feedback indicating strong demand for on-site usage capabilities and reduced eye strain during extended monitoring sessions."
          ],
          solutionImages: [
            "/img/room-dashboard.png",
            "/img/facility dashboard.png", 
            "/img/dark mode.png"
          ]
        }
      ]
    }
  },
  {
    id: 'mystoria',
    title: 'MyStoria',
    description: 'Comprehensive fertility tracking and health assessment platform for patient advocacy and care optimization.',
    image: '/img/mystoria-1.png',
    technologies: ['Health Monitoring', 'Clinical Analytics', 'Patient Support', 'Care Navigation'],
    companyLogo: 'MyStoria'
  },
  {
    id: 'disaster-tech',
    title: 'Disaster Tech Emergency Response Platform',
    description: 'Emergency response dashboard prioritizing critical information for first responders.',
    image: '/img/disaster tech-1.png',
    technologies: ['Crisis Management', 'Emergency Preparedness', 'Customizable Report', 'Real-time Alerting'],
    companyLogo: 'Disaster Tech',
    caseStudy: {
      sections: [
        {
          title: "The problem",
          content: "Emergency responders faced information overload during crisis situations, with critical data scattered across multiple systems, making it difficult to prioritize actions and coordinate effective response efforts."
        },
        {
          title: "My role",
          content: "Designed the emergency dashboard interface and information hierarchy system for first responders."
        },
        {
          title: "Project duration",
          content: "3 months"
        },
        {
          title: "Completion date",
          content: "June 2022"
        },
        {
          title: "Impact and key outcomes",
          content: "Developed a streamlined emergency response platform that improved decision-making speed and coordination during critical situations.",
          keyOutcomes: [
            "**Priority-based information** - Organized critical data by urgency and relevance to reduce cognitive load during emergencies",
            "**Real-time coordination** - Enabled instant communication and status updates between response teams",
            "**Customizable reporting** - Allowed different response units to configure dashboards based on their specific operational needs"
          ]
        },
        {
          title: "Solution",
          content: "Created an intelligent dashboard that prioritizes and presents emergency information based on severity, location, and resource availability, enabling faster decision-making and more effective crisis response coordination."
        }
      ]
    }
  }
];

// SiftedAI comprehensive case study project for featured page
export const siftedFeaturedProject: Project = {
  id: 'sifted-featured',
  title: 'SiftedAI Logistics Intelligence',
  description: '<strong>SiftedAI</strong> – Logistics intelligence with streamlined AI insights and scenario modeling.',
  tagline: 'Transforming Sifted AI\'s logistics platform through redesigned <a href="#scenario-modeling" class="tagline-highlight">scenario modeling</a> and enhanced intelligent recommendations',
image: '/img/sifted-1.jpg',
  technologies: ['Data Visualization', 'Decision Support', 'Enterprise SaaS', 'Logistics Intelligence'],
  isFeatured: true,
  companyLogo: 'SiftedAI',
  award: 'iF Design Award 2025',
  caseStudy: {
    sections: [
      {
        title: "Company",
        content: "Sifted is a SaaS company utilizing AI and predictive analytics to help shippers optimize their supply chain operations. For nearly 20 years, Sifted has been the leader in parcel spend management, evolving from basic auditing to pioneering the Logistics Intelligence software category. Their third-generation platform, SiftedAI, monitors every aspect of parcel spending—tracking billing errors, network optimization opportunities, and self-inflicted surcharges—to find ongoing savings opportunities.\n\nAs I started working on the project, Sifted's platform contained robust AI capabilities and valuable insights, but they weren't easily discoverable or actionable. The data-rich logistics platform required transformation into an intuitive, user-friendly experience without compromising its powerful functionality. Users needed to make complex supply chain decisions while reducing cognitive load, turning logistics from a costly administrative burden into a competitive advantage."
      },
      {
        title: "My role",
        content: "I co-designed the SiftedAI platform as part of a two-designer team while at Momentum Design Lab, focusing on defining key overall product strategy and user experience transformation. My primary responsibility centered on repositioning the experience around the Sifted Score as the core decision-making framework while improving AI recommendation visibility throughout the platform.\n\nI focused heavily on redesigning the scenario modeling feature, transforming how users interact with future-scenario planning and predictive analytics. Working closely with Sifted's technical teams, I ensured our design solutions could be implemented within their existing architecture while delivering the transformative experience we envisioned for this third-generation platform."
      },
      {
        title: "Project duration",
        content: "3 months"
      },
      {
        title: "Completion date",
        content: "March 2024"
      },
      {
        title: "Impact & Results",
        keyOutcomes: [
          "Redesigned the platform experience around the Sifted Score framework, creating a true command center that empowers users to make faster, data-driven decisions",
          "Enhanced AI recommendation visibility throughout the platform, making critical insights immediately discoverable and actionable for users",
          "Transformed scenario modeling workflows to reduce dependency on external consultants and democratize access to sophisticated supply chain optimization tools",
          "Won the [iF Design Award](https://ifdesign.com/en/winner-ranking/project/siftedai-a-logistics-intelligence-software/698823) and [UX Design Award](https://ux-design-awards.com/winners/2025-1-siftedai-a-logistics-intelligence-software) for User Experience, validating the platform's transformation from complex tool to intuitive logistics intelligence solution"
        ]
      },
      {
        title: "Scenario modeling",
        solutionImage: "/img/Modeling_Network.gif",
        subsections: [
          {
            title: "Overview",
            content: "Logistics is a data-heavy and multi-faceted operation. The Sifted team developed the \"Sifted Score\" that captures the essence of this operation and provides critical insights to users. Along with these insights, one key offering from the intelligence platform is allowing users to create predictive models that display potential money saved through different scenario changes.<br><br>Just like working with any other client in a design agency, I, along with my fellow designer, dove into the discovery and research phase on the current state of the application to understand the complexity and hierarchy of the platform."
          },
          {
            title: "Discovery & Research",
            content: "From our current state analysis, we identified these main usability issues with the platform:<br><br><ul><li>**Heavy information density** - users tend to get lost in the abundance of data without clear prioritization or guidance toward actionable insights.</li><li>**Over-complicated navigation** - while different functions, features, and data are interconnected, they were scattered across different sections of the application with poor logic, resulting in confusing navigation patterns.</li><li>**Not actionable** - even though the application was equipped with vast capabilities to understand data and perform actions, these CTAs were not evident. Users struggled to identify main actions throughout the app, resulting in heavy reliance on external consultancy.</li><li>**Buried AI insights** - the Sifted platform's unique offering of AI-powered insights wasn't being promoted throughout the app. Users had difficulty understanding the value of Sifted AI insights.</li></ul><br><br>Based on our current state assessment and continuous collaboration with the Sifted leadership team, we created an ideal user journey to capture the overall user flow and understand how data is gathered from users to better inform our design direction.<br><br><img src='/img/ideal-user-flow.png' alt='ideal user flow image' class='solution-step-image' />"
          },
          {
            title: "Restructuring Information Architecture",
            content: "Based on our research on the current state and ideal journey, we proposed a new IA architecture. Key changes included:<br><br><ul><li>**Insight-first navigation** that prioritizes savings, cost trends, and performance metrics to drive decision-making.</li><li>**Task-based structure** that aligns with user goals like spend analysis, contract management, and package optimization.</li><li>**Dynamic filtering and modeling tools** that enable users to explore scenarios by carrier, year, score, and other key dimensions.</li><li>**Proactive alerting system** that surfaces expiring contracts, billing issues, and other risks to prompt timely action.</li></ul><br><br><img src='/img/ia-before-after.png' alt='IA image before & after' class='solution-step-image' />"
          },
          {
            title: "Elevating the Sifted Score",
            content: "Because the platform has a wide range of data available to users, we explored different navigation schemes to identify the best direction for surfacing insights at a high level.<br><br><img src='/img/wireframe.png' alt='wireframe image' class='solution-step-image' /><br><br>One of our main goals was to elevate the Sifted Score and insights including \"recommendations,\" \"alerts,\" and \"upcoming\" at a global level that's always accessible throughout any workflow. We used a collapsible left sidebar to display the Sifted Score along with key data and insights. Since the Sifted Score is calculated from six main datasets, we implemented a spider chart to compare, analyze, and make decisions using a single graphic.<br><br><img src='/img/sifted-score.png' alt='sifted score' class='solution-step-image' />"
          },
          {
            title: "Making AI Insights Visible",
            content: "During our initial collaborative workshop, we identified the key opportunity to shift this project from surface-level UI improvements to fundamentally rethinking how users interact with logistics data. We realized that by centering the experience around the Sifted Score and making critical metrics immediately visible, we could transform how users make decisions. Throughout the application, we utilized a distinct visual treatment to highlight insights and recommendations across all different datasets to elevate the unique capability of SiftedAI.<br><br><img src='/img/green-recommendation-visuals.png' alt='combination of the green recommendation visuals' class='solution-step-image' />"
          },
          {
            title: "Redesigning Network Optimization",
            content: "Network optimization became a key opportunity to surface unique map-based insights for users to understand how different fulfillment center locations could impact logistical data. The redesign educates users on the page's goal while providing clear map visualization along with the impact on various logistical metrics.<br><br><img src='/img/before-after-network-optimization.png' alt='before after network optimization' class='solution-step-image' />"
          },
          {
            title: "Streamlining Contract Modeling",
            content: "The contract modeling feature restructured the previously long and tedious process into a simplified 4-step workflow, complete with interactive guidance and clear CTAs. This transformation reduced the complexity that previously required external consultation while maintaining the sophisticated analytical capabilities users needed.<br><br><img src='/img/contract-modeling.gif' alt='contract modeling' class='solution-step-image' />"
          }
        ]
      },
      {
        title: "The results",
        content: "The scenario modeling redesign transformed one of Sifted's most complex features into an intuitive, guided experience that empowers users to explore \"what-if\" scenarios with confidence. The new workflow breaks down complex logistics variables into digestible steps, allowing users to adjust parameters like carrier mix, service levels, and geographic distribution while immediately visualizing financial impact through the integrated Sifted Score framework.<br><br>This redesign eliminated the need for external consultants to interpret modeling results, democratizing access to sophisticated supply chain optimization tools and enabling faster decision-making across organizations of all sizes. The platform's transformation was recognized with both the iF Design Award and UX Design Award for excellence in user experience design, validating our approach to making complex logistics intelligence accessible and actionable."
      }
    ]
  }
};

// Instrumental comprehensive case study project
export const instrumentalProject: Project = {
  id: 'instrumental',
  title: 'Instrumental Manufacturing Intelligence',
  tagline: 'Elevating Instrumental platform\'s user engagement through <a href="#visual-correlation" class="tagline-highlight">AI-powered visual correlation</a>',
  image: '/img/instrumental-1.png',
  technologies: ['AI-Powered UX', 'Data Visualization', 'Manufacturing Intelligence', 'Design Systems'],
  isFeatured: true,
  caseStudy: {
    sections: [
      {
        title: "The problem",
        content: "Instrumental Inc. builds core manufacturing data infrastructure for the world's most admired electronics brands. The platform collects imaging data at every step of the assembly process and utilizes proprietary AI/ML computer vision to proactively flag anomalous units and monitor for known visual defects. With Instrumental's platform, design engineers can review assembly data and identify problems early in the process, saving effort and time, improving production efficiency, and reducing waste.\n\nWhen I joined, the platform was suffering from a user engagement problem. While a few power users from each client organization loved using Instrumental's platform, the majority of users were struggling to integrate it into their routine workflows. The platform's power was hidden behind a broken interface with numerous usability issues."
      },
      {
        title: "My role",
        content: "I led the end-to-end design of Gen 2 Solve AI, integrated with AI-powered visual correlation, from research through usability testing with beta users. I also redefined the data visualization strategy across the platform.\n\nWorking closely with engineers, I advocated for and built the design system for our web app, eventually leading the company's initiative to transition to a React-based tech stack. During my 1.5 years at Instrumental, I joined when there was only one founding designer and established weekly design review sessions while leading the overall design system development."
      },
      {
        title: "Project duration",
        content: "6 months"
      },
      {
        title: "Completion date",
        content: "Oct 2024"
      },
      {
        title: "Goal & Results",
        keyOutcomes: [
          "Designed and launched visual correlation feature as Gen 2 Solve AI at our annual Build Better manufacturing summit in 2024, driving 70% of sales conversations",
          "Built a token-based design system that eliminated 80% of inconsistencies in our web app", 
          "Partnered closely with engineering to define future design-implementation strategy alongside the transition to the new React framework"
        ]
      },
      {
        title: "Visual correlation",
        solutionImage: "/img/visualcorrelation.png",
        subsections: [
          {
            title: "Overview",
            content: "Solve AI maintained the highest risk for enterprise license dropouts due to its low usage compared to our other main offerings. We identified four key opportunities:<br><br><ul><li>Increasing visibility</li><li>Leveraging limited platform data to surface insights</li><li>Integrating unit images into correlation to enhance perceived value</li><li>Launching Deep Visual Correlation using proprietary ML algorithms to generate clustered units based on visually similar images correlated with functional test failures</li></ul>"
          },
          {
            title: "Understanding User Needs",
            content: "I interviewed customers to learn their pain points and validated directions for the visual correlation project. The research revealed that:<br><br><ul><li>Deep Visual Correlation was highly desired by all users</li><li>Increased collaboration features like star/bookmark/share would encourage more usage</li><li>Connecting correlation results with the rest of the app would be highly appealing</li><li>Data upload remained a low priority since users needed to first trust the application's capabilities</li></ul>"
          },
          {
            title: "Redefining the Workflow",
            content: "The Gen 1 correlation tool was hidden with an undiscoverable entry point, making it nearly inaccessible to users. It functioned only as a playground interaction with no further actions available, ending abruptly when users took screenshots to bring results to external platforms.\n\nThe new Deep Visual Correlation feature required time to run, so by storing correlation runs in the backend, users could create a correlation, let it run in the background, and return to review results.<br><br><img src='/img/userflow.png' alt='User workflow comparison showing before and after redesign' class='solution-step-image' />"
          },
          {
            title: "Streamlining the Setup Process",
            content: "The correlation setup process became critical as users needed to define their problem space and exploration area. This was overwhelming since there could be hundreds or thousands of different metrics, including functional test failures, uploaded supplier data, and Instrumental data.\n\nMy initial design focused on displaying all relevant data with clear categories, providing date and unit filters alongside predictor metric selection. After sharing wireframes with beta users, I gathered valuable insights: users wanted automation rather than manual exploration definition, \"select all\" should be more prominent than sub-categories, and error prevention was key since users would accidentally start empty correlations.\n\nThis feedback led to a v2 setup flow that separated unit sampling into its own step, elevated date selection, and prevented users from proceeding with zero units selected.<br><br><img src='/img/setup before after.png' alt='Correlation setup flow comparison showing v1 vs v2 design iterations' class='solution-step-image' />"
          },
          {
            title: "Optimizing the Layout",
            content: "The previous correlation view suffered from inefficient space usage and lacked responsive design for wider screens. The top-down layout also caused users to lose context of the root problem when scrolling.\n\nThe new layout used space more efficiently by leveraging a left sidebar for root metrics while defining scalable responsive behavior for data display. Through a combination of sidebar, tabs, and dropdown context switching for starred and hidden relationships, I developed a robust interface capable of handling complex relationships.<br><br><img src='/img/layout exploration.png' alt='Layout optimization showing correlation view design iterations and responsive layout solutions' class='solution-step-image' />"
          },
          {
            title: "Handling Data Complexity",
            content: "The Instrumental web app contained various data types for correlation, including strings, booleans, numbers, and combinations of numbers and strings with Pass/Fail values. I explored different options and found a middle ground that balanced complexity with simplicity.<br><br><img src='/img/relationship small card.png' alt='Data complexity visualization showing different correlation chart types for various data formats' class='solution-step-image' />"
          },
          {
            title: "Surfacing Relevant Imaging Data",
            content: "A key challenge was determining which imaging data to highlight for each relationship. When correlating P/F boolean metrics, we could surface four areas: P-P, P-F, F-P, and F-F. Complexity multiplied when root metrics could be boolean, numbers without P/F designation, or string groups, while correlating to measurements could be unlimited or have one or two limits.\n\nThrough several iterations, I reduced this complexity to surface the most relevant information—visual discrepancies most likely to correlate with potential issues.<br><br><img src='/img/correlation visual units.png' alt='Visual correlation units showing how imaging data is surfaced for different relationship types' class='solution-step-image' /><br><br><img src='/img/Large correlation cards.png' alt='Large correlation cards interface showing detailed visual correlation analysis with imaging data' class='solution-step-image' />"
          },
          {
            title: "Highlighting Deep Visual Correlation",
            content: "The flagship feature, Deep Visual Correlation, identifies visually distinct population groups using proprietary ML algorithms. While the data visualization was straightforward, the key question was how to promote its significance compared to other correlations to intrigue users.\n\nOn the summary view displaying all relationship graphs, I needed to signify that DVC was worth investigating while maintaining scannability without disrupting the grid layout. This required balancing showing more information (business requirement) with optimal user experience.<br><br><img src='/img/deep visual correlation.png' alt='Deep Visual Correlation interface showing population groups and visual analysis with imaging data' class='solution-step-image' />"
          }
        ]
      }
    ],
    lessonsLearned: "Working on visual correlation taught me that successful enterprise UX requires balancing user needs with technical realities. Early beta user research revealed that automation trumped granular control, while error prevention—like validating date ranges before correlation runs—proved far more valuable than error recovery. My main takeaway is understanding how large datasets can significantly impact interaction performance. With thousands of metrics and over ten thousand units within an active production project, displaying all information creates loading delays while derived information becomes challenging to surface quickly. Collaborating closely with engineers helped me identify these performance constraints early, enabling necessary design iterations before implementation. The project's success—driving 70% of sales conversations—came from treating technical limitations as design opportunities, simplifying complex interfaces without sacrificing functionality, and recognizing that small visual hierarchy decisions can have outsized business impact."
  }
};

// Projects with completed case studies for navigation
export const navigableProjects: Project[] = [
  ...currentProjects.filter(p => !['deep-visual-correlation', 'unit-filter', 'framework-transitioning'].includes(p.id)),
  instrumentalProject,
  siftedFeaturedProject,
  previousProjects.find(p => p.id === 'resourcex')!,
  previousProjects.find(p => p.id === 'growlink')!
];

// Legacy export for compatibility
export const projects: Project[] = [...currentProjects, ...previousProjects, instrumentalProject, siftedFeaturedProject];